import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Plus, X, ArrowLeft } from 'lucide-react';
import { crearCarta } from '../../services/cartaCompromisoService';

const CrearCarta = () => {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [clausulas, setClausulas] = useState(['']);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const validarFormulario = () => {
      const nombreValido = nombre.trim() !== '';
      const descripcionValida = descripcion.trim() !== '';
      const clausulasValidas = clausulas.length > 0 && clausulas.every(clausula => clausula.trim() !== '');
      setIsFormValid(nombreValido && descripcionValida && clausulasValidas);
    };

    validarFormulario();
  }, [nombre, descripcion, clausulas]);

  const agregarClausula = () => {
    setClausulas([...clausulas, '']);
  };

  const eliminarClausula = (index) => {
    if (clausulas.length > 1) {
      const nuevasClausulas = clausulas.filter((_, i) => i !== index);
      setClausulas(nuevasClausulas);
    } else {
      toast.warning('Debe mantener al menos una cláusula');
    }
  };

  const manejarCambioClausula = (index, valor) => {
    const nuevasClausulas = [...clausulas];
    nuevasClausulas[index] = valor;
    setClausulas(nuevasClausulas);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      toast.error('Por favor, complete todos los campos requeridos');
      return;
    }

    try {
      const cartaData = {
        nombre: nombre,
        descripcion: descripcion,
        clausulas: clausulas
      };

      const response = await crearCarta(cartaData);

      if (response.success) {
        toast.success('Carta creada exitosamente');
        setTimeout(() => {
          navigate('/admin/centro/cartas');
        }, 2000);
      } else {
        toast.error(response.message || 'Error al crear la carta');
      }
    } catch (error) {
      console.error('Error al crear la carta:', error);
      toast.error('Error al crear la carta');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/centro/cartas')}
                className="p-2 hover:bg-gray-100 rounded-full mr-4 transition-colors"
              >
                <ArrowLeft className="h-6 w-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1">Crear Nueva Carta de Compromiso</h1>
                <p className="text-gray-600">Complete la información para crear una nueva carta</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Paso 1 de 1</div>
              <div className="text-xs text-gray-400">Información básica</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">Nombre de la Carta *</label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="w-full px-4 py-4 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                  placeholder="Ej: Carta de Compromiso Académico 2024"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">Descripción *</label>
                <textarea
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  className="w-full px-4 py-4 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all duration-200 text-gray-900 placeholder-gray-400 resize-none"
                  rows="4"
                  placeholder="Describa el propósito y contexto de esta carta de compromiso..."
                  required
                />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <div className="w-1 h-6 bg-indigo-500 rounded-full mr-3"></div>
                    Cláusulas de la Carta *
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Agregue las cláusulas que formarán parte de la carta</p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                    {clausulas.length} cláusula{clausulas.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <div className="space-y-4">
                {clausulas.map((clausula, index) => (
                  <div key={index} className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all duration-200">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full text-sm font-semibold">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1 space-y-2">
                        <textarea
                          value={clausula}
                          onChange={(e) => manejarCambioClausula(index, e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-all duration-200 resize-none"
                          rows="3"
                          required
                          placeholder="Escriba el contenido de la cláusula..."
                          maxLength={500}
                        />
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">
                            {clausula.length}/500 caracteres
                          </span>
                          <button
                            type="button"
                            onClick={() => eliminarClausula(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Eliminar cláusula"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-center pt-4">
                <button
                  type="button"
                  onClick={agregarClausula}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Agregar Nueva Cláusula
                </button>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-8 border-t border-gray-100">
              <button
                type="button"
                onClick={() => navigate('/admin/centro/cartas')}
                className="px-8 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg ${
                  !isFormValid
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-xl transform hover:-translate-y-0.5'
                }`}
              >
                Crear Carta de Compromiso
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer 
        position="bottom-right" 
        toastClassName="rounded-xl shadow-lg"
        bodyClassName="font-medium"
      />
    </div>
  );
};

export default CrearCarta;